import { SmallEmeraldButton } from '@shared/components/buttons';
import { Modal } from '@shared/components/modal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ItemCodeCell } from '../../../../components/product-table/columns/item-code-cell';
import { defaultProductSearchPlaceholders, SearchInput } from '../../../../components/search-input';
import type { Product } from '../../../../data/product';
import { compare, multiSort } from '../../../../helpers/column-sort';
import { useQueryFilterFunction } from '../../../../hooks/products';
import type { Family } from '../data/family';
import { MutedBadge, mutedReason } from './muted-badge';

type Props = {
  family: Family;
  onClose: () => void;
  onAdd: (product: Product) => void;
};

export function AddProductModal({ family, onClose, onAdd }: Props) {
  const [query, setQuery] = useState('');
  // `family` is a snapshot taken when the modal opened, so its `candidates`
  // list does not refresh after a move. Track moved IDs locally to swap the
  // Move button for a confirmation indicator.
  const [movedIds, setMovedIds] = useState<Set<string>>(() => new Set());
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const handleAdd = useCallback(
    (product: Product) => {
      setMovedIds((prev) => {
        const next = new Set(prev);

        next.add(product.id);

        return next;
      });
      onAdd(product);
    },
    [onAdd]
  );

  // Build the search index over allProducts so Fuse's IDF weights match the
  // main product table — keeps search ranking consistent across views.
  const matchesQuery = useQueryFilterFunction(query, family.candidates);

  const rows = useMemo(() => {
    if (!query.trim()) {
      return family.candidates.sort(
        multiSort(
          (a, b) => compare(a.brand, b.brand),
          (a, b) => compare(a.description, b.description),
          (a, b) => compare(b.size_ounces, a.size_ounces)
        )
      );
    }

    return family.candidates.filter(matchesQuery);
  }, [query, family.candidates, matchesQuery]);

  return (
    <Modal title={`Add to ${family.name}`} visible={true} onClose={onClose} size="lg">
      <div className="mb-3">
        <SearchInput
          inputRef={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholders={defaultProductSearchPlaceholders}
        />
      </div>

      <div className="max-h-[55vh] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgb(229_231_235)]">
            <tr className="text-xs uppercase text-gray-500">
              <th className="px-2 py-1 text-left">Item Code</th>
              <th className="px-2 py-1 text-left">Product</th>
              <th className="px-2 py-1 text-right">Size</th>
              <th className="px-2 py-1 text-left">Barcode</th>
              <th className="px-2 py-1 text-right" />
            </tr>
          </thead>
          <tbody>
            {rows.map((product) => (
              <ProductRow
                key={product.id}
                product={product}
                onAdd={handleAdd}
                moved={movedIds.has(product.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

function ProductRow({
  product,
  onAdd,
  moved,
}: {
  product: Product;
  onAdd: (product: Product) => void;
  moved: boolean;
}) {
  const reason = mutedReason(product);
  // Dim each non-action cell separately — `opacity` is multiplicative, so a
  // child Move button can't undo a parent's reduced opacity.
  const dim = reason || moved ? 'opacity-60' : '';

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className={`px-2 py-1 ${dim}`}>
        <ItemCodeCell product={product} />
      </td>
      <td className={`px-2 py-1 ${dim}`}>
        <div className="text-[10px] uppercase text-gray-500">{product.brand}</div>
        <div>{product.description}</div>
      </td>
      <td className={`px-2 py-1 text-right ${dim}`}>
        <div>{product.size}</div>
        {reason && <MutedBadge reason={reason} />}
      </td>
      <td className={`px-2 py-1 text-gray-500 tabular-nums ${dim}`}>{product.barcode}</td>
      <td className="px-2 py-1 text-right">
        {moved ? (
          <span className="text-xs uppercase text-gray-500">Moved</span>
        ) : (
          <SmallEmeraldButton onClick={() => onAdd(product)}>Move</SmallEmeraldButton>
        )}
      </td>
    </tr>
  );
}
